import { Injectable } from '@angular/core';
import {
	GEOLOCATION_ERROR_PERMISSION_DENIED,
	GEOLOCATION_ERROR_TIMEOUT,
	GEOLOCATION_TIMEOUT_MS,
	IGeolocationCoordinates,
} from '../models/geolocation.models';

/**
 * Obtención de geolocalización con reintento tras grant tardío del prompt nativo (NIF-388).
 *
 * `getCurrentPosition` es one-shot con timeout: si el usuario tarda en aceptar, escucha
 * `navigator.permissions.query('geolocation')` y mantiene `watchPosition` activo (el evento
 * `change` de geolocation no es fiable en Chrome tras timeout del prompt).
 *
 * `stop()` cancela watchers; idempotente. El componente lo invoca en `ngOnDestroy`.
 */
@Injectable({
	providedIn: 'root',
})
export class GeolocationPermissionService {
	private permissionStatus: PermissionStatus | null = null;
	private permissionChangeHandler: (() => void) | null = null;
	private watchId: number | null = null;
	private grantRetryActive = false;
	private onSuccess: ((coords: IGeolocationCoordinates) => void) | null = null;
	private onError: ((error: GeolocationPositionError) => void) | null = null;

	/**
	 * Solicita la posición actual al navegador.
	 *
	 * @param onSuccess Se invoca con lat/lon cuando hay coords válidas.
	 * @param onError Solo errores definitivos (p. ej. posición no disponible). TIMEOUT y
	 *   denegación inicial no disparan este callback: el servicio reintenta en silencio.
	 */
	public requestPosition(
		onSuccess: (coords: IGeolocationCoordinates) => void,
		onError?: (error: GeolocationPositionError) => void,
	): void {
		this.onSuccess = onSuccess;
		this.onError = onError ?? null;
		this.requestLocationOnce();
	}

	/**
	 * Cancela listeners y `watchPosition`. Idempotente.
	 * El journey debe llamarlo en `ngOnDestroy` para no dejar watchers colgados.
	 */
	public stop(): void {
		this.cleanup();
		this.onSuccess = null;
		this.onError = null;
	}

	/**
	 * Intento único con `getCurrentPosition` (timeout configurable, alta precisión).
	 * Si el usuario no responde a tiempo, delega en `subscribeGrantRetry`.
	 */
	private requestLocationOnce(): void {
		navigator.geolocation.getCurrentPosition(
			(position: GeolocationPosition) => {
				this.deliverPosition(position.coords.latitude, position.coords.longitude);
			},
			(error: GeolocationPositionError) => {
				if (error.code === GEOLOCATION_ERROR_PERMISSION_DENIED || error.code === GEOLOCATION_ERROR_TIMEOUT) {
					this.subscribeGrantRetry();
					return;
				}
				this.onError?.(error);
			},
			{
				enableHighAccuracy: true,
				timeout: GEOLOCATION_TIMEOUT_MS,
				maximumAge: 0,
			},
		);
	}

	/**
	 * Tras TIMEOUT o denegación inicial, escucha grant tardío.
	 * Siempre activa `watchPosition`: el evento `change` de Permissions API en geolocation
	 * no es fiable cuando el usuario acepta el prompt nativo después del timeout.
	 */
	private subscribeGrantRetry(): void {
		if (!this.grantRetryActive) {
			this.grantRetryActive = true;
			this.setupPermissionGrantListener();
		}
		this.startWatchFallback();
	}

	/**
	 * Listener opcional vía Permissions API; acelera el reintento si el navegador emite `change`.
	 */
	private setupPermissionGrantListener(): void {
		if (!navigator.permissions?.query) {
			return;
		}
		navigator.permissions
			.query({ name: 'geolocation' as PermissionName })
			.then((status: PermissionStatus) => {
				if (status.state === 'granted') {
					this.requestLocationOnce();
					return;
				}
				if (this.permissionStatus === status) return;
				this.unsubscribePermissionChange();
				this.permissionStatus = status;
				this.permissionChangeHandler = () => {
					if (status.state === 'granted') {
						this.requestLocationOnce();
					}
				};
				// Se desuscribe en cleanup() al entregar coords (deliverPosition) o en stop().
				status.addEventListener('change', this.permissionChangeHandler);
			})
			.catch(() => undefined);
	}

	/**
	 * Observa la posición hasta el primer fix o denegación definitiva del permiso.
	 * Es el mecanismo principal de recuperación tras timeout del prompt.
	 */
	private startWatchFallback(): void {
		if (this.watchId !== null) {
			return;
		}
		this.watchId = navigator.geolocation.watchPosition(
			(position: GeolocationPosition) => {
				this.deliverPosition(position.coords.latitude, position.coords.longitude);
			},
			(error: GeolocationPositionError) => {
				if (error.code === GEOLOCATION_ERROR_PERMISSION_DENIED) {
					this.cleanupWatch();
				}
			},
			{
				enableHighAccuracy: true,
				maximumAge: 0,
			},
		);
	}

	/**
	 * Entrega coords al componente y libera watchers; idempotente ante carreras watch + permissions.
	 */
	private deliverPosition(latitude: number, longitude: number): void {
		const onSuccess = this.onSuccess;
		if (!onSuccess) return;
		this.cleanup();
		this.onSuccess = null;
		this.onError = null;
		onSuccess({ latitude, longitude });
	}

	/** Resetea retry, `watchPosition` y listener de Permissions API. */
	private cleanup(): void {
		this.cleanupWatch();
		this.unsubscribePermissionChange();
		this.grantRetryActive = false;
	}

	/** Detiene `watchPosition` si estaba activo. */
	private cleanupWatch(): void {
		if (this.watchId !== null) {
			navigator.geolocation.clearWatch(this.watchId);
			this.watchId = null;
		}
	}

	/** Quita el listener `change` registrado en setupPermissionGrantListener. */
	private unsubscribePermissionChange(): void {
		if (this.permissionStatus && this.permissionChangeHandler) {
			this.permissionStatus.removeEventListener('change', this.permissionChangeHandler);
		}
		this.permissionStatus = null;
		this.permissionChangeHandler = null;
	}
}
